import { useEffect, useRef, useState } from 'react';
import { squareName, type Side, type Square } from '../../engine';
import { RoutePainter, type GhostKind, type RoutePoint } from '../routeLight';

/** WebGL can be tried here (a browser; not the test environment). */
const webglPossible = (): boolean =>
  typeof window !== 'undefined' && typeof window.WebGLRenderingContext !== 'undefined';

/** Display position of a square, in squares from the top left: the viewer's home row at the bottom. */
export function displayPoint(sq: Square, viewer: Side): RoutePoint {
  return viewer === 'S' ? { x: sq.x + 0.5, y: 5.5 - sq.y } : { x: 5.5 - sq.x, y: sq.y + 0.5 };
}

const names = (squares: readonly Square[] | null | undefined): string | undefined =>
  squares && squares.length > 1 ? squares.map(squareName).join(' ') : undefined;

/**
 * The routes on the board: the latest turn's trail, under the pieces, and a move being previewed
 * or an Epitaph's replay. Drawn as light by a shader (src/ui/routeLight.ts), or with SVG where
 * WebGL cannot be used. The squares of each route are on the element's data attributes.
 */
export function BoardRoutes({
  viewer,
  trail,
  trailSide,
  ghost,
  ghostKind,
  occupied,
  reduceMotion,
}: {
  viewer: Side;
  /** The latest turn's route: start square, then landings. */
  trail: readonly Square[] | null | undefined;
  trailSide: Side;
  /** A route to show: start square, then landings. */
  ghost: readonly Square[] | null | undefined;
  ghostKind: GhostKind;
  /** Whether a pawn stands on a square. */
  occupied: (sq: Square) => boolean;
  reduceMotion: boolean;
}) {
  const [webgl, setWebgl] = useState(webglPossible);
  const canvas = useRef<HTMLCanvasElement>(null);
  const painter = useRef<RoutePainter | null>(null);
  const trailKey = names(trail);
  const ghostKey = names(ghost);
  // Which landings of the ghost have a pawn on them (they are ringed): not the square it starts
  // from, which the moving pawn leaves (a route back through a Looking Glass may cross it).
  const start = ghost?.[0];
  const occupiedKey = ghost
    ?.map((sq, i) =>
      i > 0 && occupied(sq) && !(start && sq.x === start.x && sq.y === start.y) ? '1' : '0',
    )
    .join('');

  useEffect(() => {
    if (!webgl || !canvas.current) return;
    const p = new RoutePainter(canvas.current, () => setWebgl(false));
    painter.current = p;
    return () => {
      p.dispose();
      painter.current = null;
    };
  }, [webgl]);

  useEffect(() => {
    if (!webgl) return;
    const points = (squares: readonly Square[]) => squares.map((s) => displayPoint(s, viewer));
    painter.current?.update({
      trail: trail && trailKey ? { points: points(trail), side: trailSide } : null,
      ghost:
        ghost && ghostKey
          ? {
              points: points(ghost),
              occupied: [...(occupiedKey ?? '')].map((c) => c === '1'),
              kind: ghostKind,
            }
          : null,
      still: reduceMotion,
    });
    // The routes are compared by their squares, not by the arrays that carry them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webgl, viewer, trailKey, trailSide, ghostKey, occupiedKey, ghostKind, reduceMotion]);

  if (webgl)
    return (
      <canvas
        ref={canvas}
        className="route-light"
        aria-hidden="true"
        data-trail={trailKey}
        data-trail-side={trailKey ? trailSide : undefined}
        data-ghost={ghostKey}
        data-ghost-kind={ghostKey ? ghostKind : undefined}
      />
    );
  return (
    <>
      {trail && trailKey && (
        <RouteLine
          squares={trail}
          viewer={viewer}
          className={`trail-layer trail-${trailSide}`}
          data={{ 'data-trail': trailKey, 'data-trail-side': trailSide }}
        />
      )}
      {ghost && ghostKey && (
        <RouteLine
          squares={ghost}
          viewer={viewer}
          className={`ghost-layer ghost-${ghostKind}`}
          data={{ 'data-ghost': ghostKey, 'data-ghost-kind': ghostKind }}
        />
      )}
    </>
  );
}

/** A route as an SVG path (100 units a square), and a dot on each landing. */
function RouteLine({
  squares,
  viewer,
  className,
  data,
}: {
  squares: readonly Square[];
  viewer: Side;
  className: string;
  data: Record<string, string>;
}) {
  const points = squares.map((s) => displayPoint(s, viewer));
  const d = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x * 100},${p.y * 100}`).join(' ');
  return (
    <svg className={className} viewBox="0 0 600 600" aria-hidden="true" {...data}>
      <path d={d} />
      {points.slice(1).map((p, i) => (
        <circle key={i} cx={p.x * 100} cy={p.y * 100} r="12" />
      ))}
    </svg>
  );
}
